import { useMemo } from "react"
import { CalendarBlank } from "@phosphor-icons/react"
import { createFileRoute, Outlet, useRouter } from "@tanstack/react-router"
import {
  CreateOptionType,
  ManageCollectionDialog,
} from "~/components/collections/dialogs/ManageCollectionDialog"
import { Module, OverviewPage } from "~/components/design-system/pages"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { Button } from "~/components/reusable/Button"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { CollectionProvider } from "~/context/CollectionProvider"
import { useCollectionGroups, useGetCollectionsWithDetails } from "~/hooks/useCollections"
import { usePageTracking } from "~/hooks/usePageTracking"

export const Route = createFileRoute("/_auth/demand-planning/noos/_layout")({
  component: RouteComponent,
})

const NOOSTab = {
  timeline: "Timeline",
  tasks: "Tasks",
} as const

const tabs: HeaderTab[] = [
  { label: NOOSTab.timeline, path: "/demand-planning/noos/timeline", icon: <CalendarBlank /> },
  // { label: NOOSTab.tasks, path: "/demand-planning/noos/tasks" },
]

function RouteComponent() {
  const router = useRouter()
  const { data: rawCollections, isLoading: detailsLoading } = useGetCollectionsWithDetails()
  const { data: collectionGroups, isLoading: groupsLoading } = useCollectionGroups()
  usePageTracking("NOOS Collections")

  const noosCollections = useMemo(
    () => rawCollections?.filter((c) => c.collectionType === "noos") ?? [],
    [rawCollections],
  )

  const providerValue = useMemo(
    () => ({ collections: noosCollections, collectionGroups: collectionGroups ?? [] }),
    [noosCollections, collectionGroups],
  )

  if (detailsLoading || groupsLoading) return <LoadingOverlay />

  const hasNoos = noosCollections.length > 0

  return (
    <Module
      breadcrumb={<BreadcrumbWrapper />}
      actions={
        <Button
          variant="neutral"
          size="sm"
          onClick={() => router.navigate({ to: "/collection-management" })}
        >
          Manage Collections
        </Button>
      }
      tabs={hasNoos ? { items: tabs, path: "/demand-planning/noos" } : undefined}
    >
      {hasNoos ? (
        <CollectionProvider value={providerValue}>
          <Outlet />
        </CollectionProvider>
      ) : (
        <OverviewPage fillContent>
          <div className="flex-1 flex flex-col gap-2 items-center justify-center">
            <img src="/no_collections.png" alt="No collection" className="w-[220px] h-[160px]" />
            <p className="heading-sm">NOOS Collections</p>
            <p className="paragraph-base text-fg-subtle text-center mb-4 w-[220px]">
              No NOOS collections yet! Create your first NOOS collection to get started.
            </p>
            <ManageCollectionDialog defaultCollectionType={CreateOptionType.NOOS} />
          </div>
        </OverviewPage>
      )}
    </Module>
  )
}
